import type { EducationTopic } from '../../../components/education/EducationDrawer.tsx';

export const ATLASSIAN_JSM_TOPICS: Record<string, EducationTopic> = {
  'atlassian.jsm.serviceDeskCount': {
    id: 'atlassian.jsm.serviceDeskCount',
    title: 'Service Desk Architecture: When to Split vs Consolidate',
    sections: [
      {
        id: 'intro',
        title: 'What is a Service Desk in JSM?',
        content: 'A Jira Service Management (JSM) Service Desk (technically a "Service Project") is a distinct container for work. It has its own customer portal, request types, SLAs, workflows, agents, and permissions.',
        bullets: [
          'It is the highest level of separation in JSM',
          'Each desk creates a silo of data and configuration',
          'Customers visit a specific portal to raise requests'
        ],
        priority: 0 // Base info, lowest priority default
      },
      {
        id: 'too-many-desks',
        title: 'Why too many desks hurts',
        content: 'Creating a separate service desk for every team (HR, IT, Facilities, Legal, Marketing, Finance...) sounds organized but creates operational debt.',
        bullets: [
          'Fragmented Reporting: You cannot easily report across multiple projects',
          'Admin Overhead: Updating a workflow or SLA requires doing it X times',
          'Customer Confusion: Users don\'t know if "My laptop is broken" goes to "IT Support" or "Hardware Team"'
        ],
        riskyExample: 'Creating 15 separate desks like "IT Hardware", "IT Software", "IT Access", "HR Benefits", "HR Payroll"...',
        goodExample: 'Consolidating into "IT Service Desk" and "HR Service Desk", using Request Types to route to specific teams.',
        priority: 10,
        matchAny: [
          { field: 'decision.severity', op: 'eq', value: 'block' },
          { field: 'decision.optionId', op: 'eq', value: '13+' }
        ]
      },
      {
        id: 'when-to-split',
        title: 'When separate desks are appropriate',
        content: 'You should create a separate service desk only when there are hard requirements for isolation.',
        bullets: [
          'Data Segregation: Legal/HR data must not be visible to IT admins',
          'Different Customers: One desk is for external clients, one for internal employees',
          'Distinct Process: The way they work is fundamentally different (e.g., Software Dev vs. Facilities)'
        ],
        priority: 7,
        matchAny: [
          { field: 'answers.privacyRequirements', op: 'eq', value: 'strict' },
          { field: 'answers.processSimilarity', op: 'eq', value: 'significantly-different' },
          { field: 'answers.processSimilarity', op: 'eq', value: 'completely-different' }
        ]
      },
      {
        id: 'unified-portal',
        title: 'The "Unified Portal" Pattern',
        content: 'The most successful organizations use fewer service desks (3-7) but use Request Types heavily. A single "IT Service Desk" can handle Hardware, Software, Access, and General IT requests.',
        bullets: [
          'Easier for customers (one place to go)',
          'Easier for agents (one queue to watch)',
          'Easier for admins (maintain one set of SLAs)'
        ],
        exception: 'If a specific team (e.g., Security) needs completely locked-down access where normal IT agents cannot see tickets, give them a separate desk.',
        tags: ['recommended'],
        priority: 5,
        matchAny: [
          { field: 'answers.customerOverlap', op: 'eq', value: 'same' },
          { field: 'answers.customerOverlap', op: 'eq', value: 'mostly-same' }
        ]
      }
    ]
  }
};
